import { EVERYONE_SENTINEL, maxBulkInvitees } from "@sp-crm/core";
import { graphqlFetcher } from "backend/fetcher";
import {
    DynamicAdvancedSearchQuery,
    ensureCorrectRegionIds,
    makeAdvancedSearchDocument,
} from "components/advanced-search/advanced-search-request";
import {
    AdvancedSearchResults,
    RenderRowActionParams,
} from "components/advanced-search/advanced-search-results";
import {
    referenceContactSearchRequest,
    SelectedReferenceContact,
    selectedReferenceContactFromRecord,
    updateContactSearchText,
} from "components/references/select-reference/reference-contact-picker";
import { SelectableUser, UserSelect } from "components/shared/user-select";
import { fancyAlert } from "components/ui/fancy-alert";
import { fancyConfirm } from "components/ui/fancy-confirm";
import { SearchInputDebounced } from "components/ui/search-input-debounced";
import { SecondaryButton } from "components/ui/secondary-button";
import { Spinner } from "components/ui/spinner";
import { produce } from "immer";
import React, { useCallback, useMemo, useState } from "react";
import { useBridgeEntityType } from "store/selectors/bridge";
import { useRegionId, useRegions } from "store/selectors/hooks";
import {
    AdvancedSearchConditionNodeType,
    AdvancedSearchEntityType,
    AdvancedSearchRequest,
    FieldConditionOperator,
} from "../../generated/graphql";

interface EventInviteePickerProps {
    onSelect: (selected: SelectedReferenceContact) => void;
    onBulkAdd: (selected: SelectedReferenceContact[]) => Promise<void>;
}

export const EventInviteePicker: React.FC<EventInviteePickerProps> = props => {
    const { onSelect, onBulkAdd } = props;

    const regionId = useRegionId();
    const regions = useRegions();
    const { entityType: communityBridgeEntity } = useBridgeEntityType(
        AdvancedSearchEntityType.Community,
    );
    const communityAvailable = communityBridgeEntity !== null;
    const { entityType: contactMetadata, allTypes: entityMetadataList } =
        useBridgeEntityType(AdvancedSearchEntityType.ReferenceContact);

    const initialSearchRequest: AdvancedSearchRequest = useMemo(
        () => referenceContactSearchRequest(communityAvailable, regionId),
        [communityAvailable, regionId],
    );
    const [searchRequest, setSearchRequest] =
        useState<AdvancedSearchRequest>(initialSearchRequest);
    const [assignedTo, setAssignedTo] = useState<SelectableUser>(EVERYONE_SENTINEL);
    const [bulkBusy, setBulkBusy] = useState(false);

    const handleSearchInput = useCallback(
        (value: string) => {
            setSearchRequest(prev => updateContactSearchText(prev, value));
        },
        [setSearchRequest],
    );

    const handleAssignedChange = useCallback((user: SelectableUser) => {
        setAssignedTo(user);
        setSearchRequest(prev =>
            produce(prev, draft => {
                draft.condition.children = draft.condition.children.filter(
                    child => child.fieldName !== "ownerId",
                );
                if (user && user !== EVERYONE_SENTINEL) {
                    draft.condition.children.push({
                        nodeType: AdvancedSearchConditionNodeType.FieldCondition,
                        fieldName: "ownerId",
                        operator: FieldConditionOperator.StringEquals,
                        textValue: user,
                    });
                }
            }),
        );
    }, []);

    const handleAddAll = useCallback(async () => {
        if (!contactMetadata) {
            return;
        }
        setBulkBusy(true);
        try {
            const document = makeAdvancedSearchDocument(
                contactMetadata,
                searchRequest.select,
                entityMetadataList,
            );
            const params = ensureCorrectRegionIds(
                { ...searchRequest, page: 0, pageSize: maxBulkInvitees },
                regions,
            );
            const data = await graphqlFetcher<
                DynamicAdvancedSearchQuery,
                { params: AdvancedSearchRequest }
            >(document, { params })();
            const total = data.advancedSearch.total;
            if (total > maxBulkInvitees) {
                await fancyAlert(
                    "Too many matches",
                    `${total} contacts match. Adding everyone at once works with up to ${maxBulkInvitees} matches. Refine your search or filters first, or add guests individually.`,
                    "warning",
                );
                return;
            }
            if (total === 0) {
                await onBulkAdd([]);
                return;
            }
            const confirmed = await fancyConfirm(
                "Add all matching",
                `Add ${total} ${total === 1 ? "guest" : "guests"} to the guest list?`,
                "Add them",
                "Cancel",
            );
            if (!confirmed) {
                return;
            }
            await onBulkAdd(
                data.advancedSearch.entities.map(selectedReferenceContactFromRecord),
            );
        } catch {
            await fancyAlert(
                "Could not add guests",
                "Something went wrong finding those guests. Please try again.",
                "warning",
            );
        } finally {
            setBulkBusy(false);
        }
    }, [contactMetadata, entityMetadataList, searchRequest, regions, onBulkAdd]);

    const renderRowAction = useCallback(
        (params: RenderRowActionParams) => {
            return (
                <SecondaryButton
                    className="text-sm"
                    onClick={e => {
                        e.preventDefault();
                        onSelect(selectedReferenceContactFromRecord(params.record));
                    }}>
                    Add
                </SecondaryButton>
            );
        },
        [onSelect],
    );

    return (
        <div className="space-y-3">
            <div className="flex flex-wrap items-end gap-3">
                <div className="min-w-48 flex-1">
                    <SearchInputDebounced
                        initial=""
                        onChange={handleSearchInput}
                        placeholder="Search for contact"
                        autoFocus
                    />
                </div>
                <div className="w-56">
                    <UserSelect
                        label="Assigned to"
                        value={assignedTo}
                        onChange={handleAssignedChange}
                        includeEveryone
                    />
                </div>
                <div className="flex items-center gap-2">
                    {bulkBusy ? <Spinner /> : null}
                    <SecondaryButton disabled={bulkBusy} onClick={handleAddAll}>
                        Add all matching
                    </SecondaryButton>
                </div>
            </div>
            <AdvancedSearchResults
                request={searchRequest}
                onChangeRequest={setSearchRequest}
                renderRowAction={renderRowAction}
            />
        </div>
    );
};
